import PropTypes from 'prop-types'
import { forwardRef, memo, useCallback, useMemo, useState } from 'react'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import Alert from '../Alert/Alert'
import useFormFieldValidator from '../hooks/useFormFieldValidator'
import styles from './Checkbox.module.scss'

/**
 * The `Checkbox`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const Checkbox = forwardRef((props, forwardedRef) => {
  const {
    checked,
    className,
    label,
    onChange,
    style,
    validate,
    validationErrorMessage,
    ...rest
  } = props
  const ref = useFallbackRef(forwardedRef)
  const [internalChecked, setChecked] = useState(checked || false)
  const [isFocus, setIsFocus] = useState(false)
  // Use the form field validator
  const { hasValidationError, status } = useFormFieldValidator(ref, validate)
  const classNameOutput = useMemo(
    () =>
      classNames([
        styles.container,
        isFocus && styles.focus,
        styles[`status-${status}`],
        styles[`style-${style}`],
        hasValidationError && styles.error,
        checked !== null
          ? checked && styles.checked
          : internalChecked && styles.checked
      ]),
    [isFocus, internalChecked, checked, status, hasValidationError]
  )

  /**
   * Handle change
   */
  const handleChange = useCallback(
    e => {
      setChecked(e.target.checked)
      onChange(e)
    },
    [onChange]
  )

  /**
   * Handle focus
   */
  const handleFocus = useCallback(() => {
    setIsFocus(true)
  }, [])

  /**
   * Handle blur
   */
  const handleBlur = useCallback(() => {
    setIsFocus(false)
  }, [])

  return (
    <div className={classNameOutput}>
      <label className={styles.label} onFocus={handleFocus} onBlur={handleBlur}>
        <input
          type="checkbox"
          ref={ref}
          className={`${styles.field} ${className}`}
          checked={checked !== null ? checked : internalChecked}
          onChange={handleChange}
          {...rest}
        />
        <div className={styles.bg} />
        <span>{label}</span>
      </label>
      {hasValidationError && (
        <Alert isActive={hasValidationError}>{validationErrorMessage}</Alert>
      )}
    </div>
  )
})

Checkbox.displayName = 'Checkbox'

/** @type {object} */
Checkbox.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onChange: PropTypes.func,
  validate: PropTypes.func,
  validationErrorMessage: PropTypes.string,
  style: PropTypes.string
}

/** @type {object} */
Checkbox.defaultProps = {
  checked: null,
  className: '',
  label: '',
  onChange: noop,
  validate: null,
  validationErrorMessage: 'Please complete this field.',
  style: 'checkbox'
}

// Memoize
export default memo(Checkbox)
